import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Copy, KeyRound, ShieldOff, RadioTower, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api, { timeAgo, formatApiErrorDetail } from "../lib/api";
import { StatusBadge } from "../components/badges";

const CAPS = ["STATUS", "TASK", "RESULT", "ALERT", "METRICS", "EVENT"];
const EMPTY = { project_id: "", name: "", type: "", api_url: "", environment: "production", capabilities: [...CAPS], permissions: { can_receive_tasks: false, can_send_alerts: true, can_write_memory: false } };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [newKey, setNewKey] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/projects/register", form);
      setOpen(false);
      setForm(EMPTY);
      setNewKey({ project: data.project.name, key: data.api_key });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async (pid) => {
    if (!window.confirm(`⚠️ RÉGÉNÉRER la clé de ${pid.toUpperCase()} ?\n\nCeci RÉVOQUE IMMÉDIATEMENT la clé actuelle : le projet sera rejeté (401) jusqu'à ce que tu donnes la nouvelle clé à son agent.\n\nCe bouton ne sert PAS à consulter la clé existante (elle n'est jamais réaffichable).`)) return;
    try {
      const { data } = await api.post(`/projects/${pid}/keys/regenerate`);
      setNewKey({ project: pid, key: data.api_key });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deactivate = async (pid) => {
    if (!window.confirm(`⚠️ DÉSACTIVER toutes les clés de ${pid.toUpperCase()} ?\n\nLe projet sera rejeté (401) et ne pourra plus émettre vers NEXUS tant qu'une nouvelle clé n'aura pas été générée et transmise à son agent.`)) return;
    try {
      await api.post(`/projects/${pid}/keys/deactivate`);
      toast.success(`Clés API de ${pid.toUpperCase()} désactivées`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const ping = async (pid) => {
    try {
      const { data: res } = await api.get(`/projects/${pid}/status`);
      res.delivered && res.identity_valid
        ? toast.success(`${pid.toUpperCase()} EN LIGNE — identité validée`)
        : toast.error(`${pid.toUpperCase()} injoignable`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeProject = async (pid) => {
    if (!window.confirm(`Retirer définitivement ${pid.toUpperCase()} du registre ? Ses clés seront révoquées.`)) return;
    try {
      await api.delete(`/projects/${pid}`);
      toast.success(`${pid.toUpperCase()} retiré du registre`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCap = (c) =>
    setForm((f) => ({ ...f, capabilities: f.capabilities.includes(c) ? f.capabilities.filter((x) => x !== c) : [...f.capabilities, c] }));

  const copyKey = () => {
    navigator.clipboard.writeText(newKey.key);
    toast.success("Clé copiée dans le presse-papiers");
  };

  const inputCls = "w-full border border-border bg-[#050505] px-3 py-2 font-mono text-sm text-white outline-none transition-colors duration-150 focus:border-primary";

  return (
    <div data-testid="projects-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Projets</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Registre des projets connectés</p>
        </div>
        <button
          data-testid="register-project-button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 bg-primary px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black shadow-[0_0_18px_rgba(0,229,255,0.25)] transition-opacity duration-150 hover:opacity-90"
        >
          <Plus size={14} /> Enregistrer un projet
        </button>
      </div>

      <div className="overflow-x-auto border border-white/10 bg-card shadow-[0_0_20px_rgba(0,229,255,0.04)]">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="px-4 py-3">Projet</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Endpoint API</th>
              <th className="px-4 py-3">Capacités</th>
              <th className="px-4 py-3">Dernière comm.</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {projects.map((p) => (
              <tr key={p.project_id} data-testid={`project-row-${p.project_id}`} className="transition-colors duration-100 hover:bg-muted/50">
                <td className="px-4 py-3">
                  <Link to={`/projects/${p.project_id}`} className="font-heading font-bold uppercase tracking-wider text-white transition-colors duration-150 hover:text-primary">
                    {p.name}
                  </Link>
                  <div className="font-mono text-[10px] text-neutral-600">{p.project_id}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-400">{p.type}
                  <div className={`font-mono text-[9px] uppercase tracking-[0.1em] ${p.environment === "production" ? "text-[#00E676]" : p.environment === "simulator" ? "text-amber-300" : "text-neutral-600"}`}>{p.environment || "test"}</div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="max-w-[220px] truncate px-4 py-3 font-mono text-[10px] text-neutral-500">{p.api_url}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-neutral-400">{p.capabilities.join(" · ")}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-400">{timeAgo(p.last_seen_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button data-testid={`ping-${p.project_id}`} onClick={() => ping(p.project_id)} title="Ping STATUS"
                      className="border border-border p-1.5 text-primary transition-colors duration-150 hover:border-primary/50">
                      <RadioTower size={13} />
                    </button>
                    <button data-testid={`regen-key-${p.project_id}`} onClick={() => regenerate(p.project_id)} title="Régénérer la clé API"
                      className="border border-border p-1.5 text-amber-300 transition-colors duration-150 hover:border-amber-500/50">
                      <KeyRound size={13} />
                    </button>
                    <button data-testid={`deactivate-key-${p.project_id}`} onClick={() => deactivate(p.project_id)} title="Désactiver les clés"
                      className="border border-border p-1.5 text-red-400 transition-colors duration-150 hover:border-red-500/50">
                      <ShieldOff size={13} />
                    </button>
                    <button data-testid={`delete-project-${p.project_id}`} onClick={() => removeProject(p.project_id)} title="Retirer du registre"
                      className="border border-border p-1.5 text-neutral-500 transition-colors duration-150 hover:border-red-500/50 hover:text-red-400">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-primary/25 bg-[#0f0f0f] text-white shadow-[0_0_40px_rgba(0,229,255,0.1)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-widest">ENREGISTRER UN PROJET</DialogTitle>
            <DialogDescription className="font-mono text-xs text-neutral-500">Crée la fiche du projet et génère sa clé API (affichée une seule fois).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">ID du projet</label>
                <input data-testid="form-project-id" required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={inputCls} placeholder="publia" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Nom</label>
                <input data-testid="form-project-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Publia" />
              </div>
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Type</label>
              <input data-testid="form-project-type" required value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} placeholder="social_media_ai" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">URL de l'API</label>
              <input data-testid="form-project-url" required value={form.api_url} onChange={(e) => setForm({ ...form, api_url: e.target.value })} className={inputCls} placeholder="https://projet.example.com/api/nexus" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Environnement</label>
              <select data-testid="form-project-environment" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} className={inputCls}>
                {["production", "preview", "test", "simulator"].map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Capacités</label>
              <div className="flex flex-wrap gap-2">
                {CAPS.map((c) => (
                  <button type="button" key={c} data-testid={`cap-${c.toLowerCase()}`} onClick={() => toggleCap(c)}
                    className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-150 ${form.capabilities.includes(c) ? "border-primary/50 bg-primary/10 text-primary" : "border-border text-neutral-500"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Permissions</label>
              {Object.entries({ can_receive_tasks: "Peut recevoir des tâches", can_send_alerts: "Peut envoyer des alertes", can_write_memory: "Peut écrire en mémoire" }).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 font-mono text-xs text-neutral-300">
                  <input type="checkbox" data-testid={`perm-${k}`} checked={form.permissions[k]}
                    onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [k]: e.target.checked } })}
                    className="accent-[#00E5FF]" />
                  {label}
                </label>
              ))}
            </div>
            <button data-testid="form-submit-button" type="submit" disabled={saving}
              className="w-full bg-primary py-2.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-opacity duration-150 hover:opacity-90 disabled:opacity-50">
              {saving ? "Enregistrement…" : "Enregistrer et générer la clé"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent className="border-primary/25 bg-[#0f0f0f] text-white shadow-[0_0_40px_rgba(0,229,255,0.1)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-widest">CLÉ API — {newKey?.project?.toUpperCase()}</DialogTitle>
            <DialogDescription className="font-mono text-xs text-neutral-500">Copie cette clé maintenant — elle ne sera plus jamais affichée.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border border-amber-500/40 bg-amber-500/10 px-3 py-2 font-mono text-xs text-amber-300">
              ⚠ Cette clé ne sera plus jamais affichée. Conservez-la en lieu sûr.
            </div>
            <div data-testid="api-key-display" className="break-all border border-primary/40 bg-[#050505] px-4 py-3 font-mono text-sm text-primary">
              {newKey?.key}
            </div>
            <button data-testid="copy-key-button" onClick={copyKey}
              className="flex w-full items-center justify-center gap-2 border border-border py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-150 hover:border-primary/50 hover:text-primary">
              <Copy size={13} /> Copier la clé
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
